<script lang="ts">
import { useCtaComponent } from '@monogrid/vue-boost/core'
import { templateRef } from '@vueuse/core'
import { defineComponent, PropType } from 'vue'

import { useTracking } from '@/functions/useTracking'
import { Theme } from '@/types'

export default defineComponent({
  name: 'CtaButton',
  props: {
    negative: { type: Boolean, default: false },
    primary: { type: Boolean, default: false },
    secondary: { type: Boolean, default: false },
    blackOutline: { type: Boolean, default: false },
    tertiary: { type: Boolean, default: false },
    theme: { type: String as PropType<`${Theme}`>, default: undefined }
  },
  setup (props, context) {
    const cta = templateRef<HTMLElement>('cta')
    const { bindings, component } = useCtaComponent(context)

    useTracking(cta, { event: 'holidayExperienceCta' })

    return {
      bindings,
      component
    }
  }
})
</script>

<template>
  <component
    :is="component"
    ref="cta"
    v-bind="bindings"
    class="cta inline-flex justify-center items-center relative"
    :class="{
      'cta-dark': theme === 'light',
      'cta-light': theme === 'dark',
      negative,
      primary: primary && !secondary,
      secondary: secondary && !primary,
      tertiary: tertiary && (!primary && !secondary),
      'black-outline': blackOutline
    }"
  >
    <slot name="content">
      <span class="over-h relative z-1">
        <slot />
      </span>
    </slot>
  </component>
</template>

<style lang="scss" scoped>
.cta {
  border: none;
  appearance: none;
  background: transparent;
  color: var(--cta-bg-color, inherit);
  cursor: pointer;
  font-family: 'Acqua di Parma', sans-serif;
  text-decoration: none;
  transition: background-color $default-duration, color $default-duration;

  &:not(.primary, .secondary) {
    padding: 0 0 4px;

    &::after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      border-bottom: dotted 2px currentColor;
      content: '';
    }
  }

  [data-theme="dark"] &:not([disabled], .disabled) {
    --cta-bg-color: #{$c-white};
    --cta-txt-color: #{$c-black};

    &.negative {
      --cta-bg-color: #{$c-black};
      --cta-bg-hover-color: #{$c-brown};
      --cta-txt-color: #{$c-white};
    }
  }

  [data-theme="light"] &:not([disabled], .disabled) {
    --cta-bg-color: #{$c-black};
    --cta-bg-hover-color: #{$c-brown};
    --cta-txt-color: #{$c-white};

    &.negative {
      --cta-bg-color: #{$c-white};
      --cta-bg-hover-color: #{$c-white};
      --cta-txt-color: #{$c-black};
    }
  }

  &.cta-dark:not([disabled], .disabled) {
    --cta-bg-color: #{$c-black};
    --cta-bg-hover-color: #{$c-brown};
    --cta-txt-color: #{$c-white};
  }

  &.cta-light:not([disabled], .disabled) {
    --cta-bg-color: #{$c-white};
    --cta-txt-color: #{$c-black};
  }

  &[disabled], &.disabled {
    --cta-bg-color: #{$c-gray};
    --cta-txt-color: #{$c-black};

    cursor: default;
    pointer-events: none;
  }

  &.primary, &.secondary {
    color: var(--cta-txt-color);

    @include hover {
      &:hover::before {
        background-color: var(--cta-bg-hover-color, var(--cta-bg-color));
      }
    }
  }

  &.primary {
    min-width: rem(48px);
    min-height: rem(34px);
    padding: rem(4px 12px);

    &::before {
      position: absolute;
      z-index: 0;
      top: 0;
      left: rem(6px);
      width: calc(100% - rem(12px));
      height: 100%;
      border-radius: rem(4px);
      background-color: var(--cta-bg-color);
      content: '';
      transition: background-color $default-duration;
    }
  }

  &.secondary {
    min-height: rem(28px);
    padding: rem(12px 24px);
    font-weight: 600;

    &::before {
      position: absolute;
      z-index: 0;
      top: 0;
      left: rem(8px);
      width: calc(100% - rem(16px));
      height: 100%;
      border-radius: rem(24px);
      background-color: var(--cta-bg-color);
      content: '';
      transition: background-color $default-duration;
    }

  &.black-outline {
    color: $c-black;
    font-weight: 700;
    transition: color $default-duration, background-color $default-duration;

    &::before {
      border: 1px solid $c-black;
      background-color: transparent;
    }

    /* stylelint-disable */
    @include hover {
      &:hover {
        color: $c-white;

        &::before {
          background-color: $c-black;
        }
      }
    /* stylelint-enable */
    }
  }
}

  &.tertiary {
    padding: rem(4px 12px);

    &::after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      border-bottom: none;
      content: '';
    }
  }

  & > .over-h {
    padding-top: 0.1em;
    line-height: 0.9;
  }

  &.black-outline > .over-h {
    padding-top: 0.25em;
  }
}

.cta-decoration {
  position: absolute;
  z-index: 0;
  border-radius: 50%;
  background-color: var(--cta-bg-color);

  &:first-child {
    left: -3px;
  }

  &:last-child {
    right: -3px;
  }

  .primary & {
    top: calc(50% - rem(12px));
    width: rem(24px);
    height: rem(24px);
    transition: background-color $default-duration;

    &::before,
    &::after {
      position: absolute;
      width: rem(4px);
      height: rem(4px);
      border-radius: 50%;
      background-color: var(--cta-bg-color);
      content: '';
      transform: scale(0.5);
      transition: transform $default-duration, background-color $default-duration;
    }

    &::before {
      top: rem(-4px);
    }

    &::after {
      bottom: rem(-4px);
    }

    &:first-child::before,
    &:first-child::after {
      left: 0;
    }

    &:last-child::before,
    &:last-child::after {
      right: 0;
    }
  }

  .black-outline & {
    background-color: $c-black;
  }

  .secondary & { // stylelint-disable-line
    top: calc(50% - 4px);
    width: 8px;
    height: 8px;
    transform: scale(0.6);
    transition: transform $default-duration, background-color $default-duration;

    &:first-child {
      transform-origin: 0% 50%;
    }

    &:last-child {
      transform-origin: 100% 50%;
    }
  }

  @include hover {
    .cta:hover & {
      background-color: var(--cta-bg-hover-color, var(--cta-bg-color));
      transform: none;

      &::before,
      &::after {
        background-color: var(--cta-bg-hover-color, var(--cta-bg-color));
        transform: none;
      }
    }

    .cta.black-outline:hover & {
      background-color: $c-black;
      transform: none;

      &::before,
      &::after {
        background-color: $c-black;
        transform: none;
      }
    }
  }
}
</style>
